import { AxiosError } from "axios";
import { AdminCarrierBridgeResponse } from "./CarrierBridgeData";
import { CarrierBridgePayload, PortOption } from "./request/request";

/**
 * The create/edit form of the Carrier Bridge admin (CTX-724), kept free of
 * React so its rules can be tested on their own: what the form starts with,
 * what makes it valid, what it sends, and how a backend refusal reads.
 */

/** Everything the form edits, as the inputs hold it (strings). */
export interface CarrierBridgeFormState {
  portId: string;
  carrierId: string;
  circuitId: string;
  vlan: string;
  /** True sends a null VLAN: the whole port goes to the carrier. */
  wholePort: boolean;
  mbps: string;
  name: string;
  isDraft: boolean;
}

export type CarrierBridgeFormErrors = Partial<
  Record<keyof CarrierBridgeFormState, string>
>;

const VLAN_MIN = 1;
const VLAN_MAX = 4094;

export const emptyCarrierBridgeForm: CarrierBridgeFormState = {
  portId: "",
  carrierId: "",
  circuitId: "",
  vlan: "",
  wholePort: false,
  mbps: "",
  name: "",
  isDraft: false,
};

export function formFromService(
  service: AdminCarrierBridgeResponse
): CarrierBridgeFormState {
  return {
    portId: service.kontrolEdgePortId != null ? String(service.kontrolEdgePortId) : "",
    carrierId: service.carrierId != null ? String(service.carrierId) : "",
    circuitId: service.circuitId ?? "",
    vlan: service.vlan ?? "",
    wholePort: service.vlan === null,
    mbps: String(service.mbps),
    name: service.name ?? "",
    isDraft: service.isDraft,
  };
}

const isPositiveInteger = (value: string) => /^\d+$/.test(value.trim()) && Number(value) > 0;

/**
 * Mirrors the backend's own checks (chopper-backend CTX-714 / CTX-717) so the
 * common mistakes show on the field before a round trip. The backend stays
 * the authority: whatever it refuses still reaches the form as an alert.
 */
export function validateCarrierBridgeForm(
  form: CarrierBridgeFormState
): CarrierBridgeFormErrors {
  const errors: CarrierBridgeFormErrors = {};

  if (!form.portId) errors.portId = "Pick the customer port";
  if (!form.carrierId) errors.carrierId = "Pick a carrier";
  if (!isPositiveInteger(form.mbps)) errors.mbps = "Enter a whole number of Mbps";

  if (!form.wholePort) {
    const vlan = form.vlan.trim();
    const number = Number(vlan);
    if (!/^\d+$/.test(vlan) || number < VLAN_MIN || number > VLAN_MAX) {
      errors.vlan = `Enter a VLAN between ${VLAN_MIN} and ${VLAN_MAX}, or use the whole port`;
    }
  }

  return errors;
}

function toFullPayload(form: CarrierBridgeFormState): CarrierBridgePayload {
  const name = form.name.trim();
  return {
    kontrolEdgePortId: Number(form.portId),
    carrierId: Number(form.carrierId),
    mbps: Number(form.mbps),
    vlan: form.wholePort ? null : String(Number(form.vlan.trim())),
    circuitId: form.circuitId.trim() || null,
    // Omitted when blank so the backend names it "Carrier Bridge - <id>".
    ...(name ? { name } : {}),
    isDraft: form.isDraft,
  };
}

/**
 * The body to send. On create, every field; on edit, only the ones that
 * changed, so an untouched port or VLAN never re-triggers the backend's port
 * check and the audit log records only real changes.
 */
export function buildCarrierBridgePayload(
  form: CarrierBridgeFormState,
  original?: CarrierBridgeFormState
): CarrierBridgePayload {
  const full = toFullPayload(form);
  if (!original) return full;

  const before = toFullPayload(original);
  const changed: CarrierBridgePayload = {};
  (Object.keys(full) as Array<keyof CarrierBridgePayload>).forEach((key) => {
    if (full[key] !== before[key]) {
      Object.assign(changed, { [key]: full[key] });
    }
  });
  // A name cleared on edit is simply not sent: the service keeps its name
  // rather than ending up blank (only create fills in a default).
  return changed;
}

/** "10-7E2C9A · MX1 · TenGigE0/0/0/5" — enough to tell ports apart. */
export function portLabel(port: PortOption): string {
  return [
    port.subid ?? port.name ?? `#${port.id}`,
    port.datacenter?.displayName || port.datacenter?.name || port.datacenter?.datacenterId,
    port.portAllocation?.portNumber,
  ]
    .filter(Boolean)
    .join(" · ");
}

export function portTenantName(port: PortOption | undefined): string {
  return port?.tenant?.nameDisplay || port?.tenant?.Name || "";
}

interface ApiErrorBody {
  code?: string;
  message?: string;
  conflictsWith?: { type?: string; subscriptionId?: string | null };
}

/**
 * What the admin reads when the backend refuses a save or a delete. The
 * backend already words its conflicts precisely (which service holds the
 * VLAN, until when a subscription runs); this keeps that message and only
 * falls back when there is none.
 */
export function describeCarrierBridgeError(error: unknown): {
  code: string | null;
  message: string;
} {
  if (error instanceof AxiosError) {
    const body = error.response?.data as ApiErrorBody | undefined;
    if (body?.message) return { code: body.code ?? null, message: body.message };
    if (error.response) {
      return { code: null, message: `The server answered ${error.response.status}` };
    }
  }
  return { code: null, message: "Could not reach the server. Try again." };
}
